import {
  CSSProperties,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { DATASET_CREATION_V2 } from "helpers/constants/filters";
import { ROISquare } from "../ROISquare/ROISquare";
import { calculateRoiSquareSize } from "../../../helpers/utility/calculateRoiSquareSize";

interface ImageLoaderProps {
  src: string;
  className?: string;
  onLoad?: (e: any) => void;
  onError?: () => void;
  "data-testid"?: string;
  style?: CSSProperties;
  alt?: string;
}

const ImageLoader = forwardRef(
  (
    {
      src,
      className,
      onLoad,
      onError,
      "data-testid": dataTestId,
      style,
      alt,
    }: ImageLoaderProps,
    ref: any,
  ) => {
    return (
      <img
        ref={ref}
        data-testid={dataTestId}
        className={className}
        src={src}
        alt={alt}
        onLoad={onLoad}
        onError={onError}
        style={style}
      />
    );
  },
);

interface ImageWithFallbackProps {
  thumbImage: { src: string };
  plainImage: { src: string };
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  alt?: string;
}

const ImageWithFallback = ({
  thumbImage,
  plainImage,
  className,
  onLoad,
  onError,
  alt,
}: ImageWithFallbackProps) => {
  const imgRef = useRef<any>(null);
  const shouldUsePlainImageAndCropToCenter =
    useFeatureFlagService(DATASET_CREATION_V2).enabled;
  const [roiSquareSize, setRoiSquareSize] = useState(0);
  const [useThumb, setUseThumb] = useState(!shouldUsePlainImageAndCropToCenter);
  const [style, setStyle] = useState<CSSProperties | undefined>(
    shouldUsePlainImageAndCropToCenter ? { display: "none" } : undefined,
  );
  const src = useMemo(
    () => (useThumb && thumbImage?.src ? thumbImage.src : plainImage.src),
    [thumbImage?.src, plainImage.src, useThumb],
  );

  const handleError = useCallback(() => {
    console.error(`Failed to load image: ${src}. Falling back to plain image.`);
    setUseThumb(false);
    onError?.();
  }, [onError, src]);

  const handleLoad = useCallback(
    (e: any) => {
      const { naturalHeight, naturalWidth } = e.target;
      if (shouldUsePlainImageAndCropToCenter) {
        const min = Math.min(naturalHeight, naturalWidth);
        const scale = Math.max(1, min / 512); // 256 from center to each side
        setStyle({ transform: `scale(${scale})`, display: "block" });
        setTimeout(() => {
          const size = calculateRoiSquareSize(imgRef, e) * scale;
          setRoiSquareSize(size);
        }, 250);
      }
      onLoad?.();
    },
    [onLoad, shouldUsePlainImageAndCropToCenter],
  );

  return (
    <>
      <ImageLoader
        ref={imgRef}
        src={src}
        className={className}
        onLoad={handleLoad}
        onError={handleError}
        data-testid={"image-with-fallback"}
        style={style}
        alt={alt}
      />
      {shouldUsePlainImageAndCropToCenter && <ROISquare size={roiSquareSize} />}
    </>
  );
};

export default ImageWithFallback;
